import { I18nextProvider, useTranslation } from 'react-i18next';
import { updateI18nSingleton } from '@cx-web/server-utils';
import { getRepubMetaFromRepub, isErrorWithStatus } from '@cx-web/utils';
import { useRouteError, useRouteLoaderData } from '@remix-run/react';
import { captureRemixErrorBoundaryError } from '@sentry/remix';
import { createInstance } from 'i18next';

import { type Repub } from '@cx-modules/types';

import RootErrorPage, { ErrorPageStyles } from '~/components/composites/RootErrorPage';
import { RepubProvider, useRepubContext } from '~/providers/repub-context';
import { REPUB_CONFIG } from '~/repub-config';
import type { loader } from '~/root-loader.ts';
import ocTheme from '~/themes/OC-theme.css';
import usTheme from '~/themes/US-theme.css';
import type { FontAttribs } from '~/types';

const errorI18n = createInstance();
updateI18nSingleton(errorI18n, 'en-US', {});

const Favicon = ({ resourcesUrl }: {resourcesUrl: string;}) => {
  const faviconPath = `${resourcesUrl}/assets/img/`;
  const { repub } = useRepubContext();

  const faviconFile = REPUB_CONFIG[repub]?.favicon || 'oc-favicon.svg';
  const favicon = `${faviconPath}${faviconFile}`;

  return (
    <>
      <link as="image" href={favicon} key={`preload-${favicon}`} rel="preload" />
      <link href={favicon} key={`icon-${favicon}`} rel="icon" />
    </>);

};

const Fonts = () => {
  const baseFonts: FontAttribs[] = [
  // Public Sans, Open Sans, Lato
  {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com'
  },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous'
  },
  // Futura PT Condensed
  {
    rel: 'preconnect',
    href: 'https://use.typekit.net'
  }];


  return (
    <>
      {baseFonts.map((data) =>
      <link key={`${data.rel}-${data.href}`} {...data} />
      )}
    </>);

};

function ErrorBoundaryContent() {
  const { ready, t } = useTranslation('label');
  const translationReady = ready;
  const error = useRouteError();
  let exception;
  if (!error) {
    exception = new Error('Unknown error caught by RootErrorBoundary');
  } else if (error instanceof Error) {
    exception = error;
  } else {
    const err = error as any;
    exception = err.error || err.message || err.originalError || err;
  }
  captureRemixErrorBoundaryError(exception);

  const rootLoaderData = useRouteLoaderData<typeof loader>('root');
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }
  const repub = rootLoaderData?.repub as Repub;
  const { repubHomeUrl: baseUrl, repubIs } = getRepubMetaFromRepub(repub);

  const errorStatus = isErrorWithStatus(error) ? error.status : null;
  const statusLogoPng = errorStatus === 404 ? 'us-404.png' : 'us-500.png';

  // Redirect to external 404 page if configured (e.g., GZ → Gazzetta's 404)
  const redirectUrl = REPUB_CONFIG[repub]?.plus?.errorPage?.redirectUrl;
  if (redirectUrl && errorStatus === 404) {
    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta content={`0;url=${redirectUrl}`} httpEquiv="refresh" />
        </head>
        <body />
      </html>);

  }

  return (
    <html lang="en">
      <RepubProvider repub={repub}>
        <head>
          <meta charSet="utf-8" />
          <meta content="width=device-width,initial-scale=1" name="viewport" />
          <Favicon resourcesUrl={rootLoaderData?.resourcesUrl ?? ''} />
          <Fonts />
          {[repubIs.OC ? ocTheme : usTheme].map((href) => [
          <link as="style" href={href} key={`${href}-preload`} rel="preload" />,
          <link href={href} key={`${href}-stylesheet`} rel="stylesheet" />]
          )}
          <ErrorPageStyles repub={repub} translationReady={translationReady} />
        </head>
        <body>
          <RootErrorPage
            baseUrl={baseUrl}
            errorStatus={errorStatus}
            repub={repub}
            rootLoaderData={rootLoaderData}
            statusLogoPng={statusLogoPng}
            t={t}
            translationReady={translationReady} />

        </body>
      </RepubProvider>
    </html>);

}

export function RootErrorBoundary() {
  const rootLoaderData = useRouteLoaderData<typeof loader>('root');

  if (rootLoaderData?.resources) {
    updateI18nSingleton(errorI18n, rootLoaderData.lng, rootLoaderData.resources);
  }

  return (
    <I18nextProvider i18n={errorI18n}>
      <ErrorBoundaryContent />
    </I18nextProvider>);

}